import React, { useEffect, useState } from 'react'
import { Company, appModules } from '@/modules/company/domain/ICompany'
import { companyCanAccessModule } from '@/utils/company'
import { Icon, IIcon, SidebarContainer, SidebarElement } from '@umahealth/occipital/client'
import { cn } from '@/utils/cn'
import { removeAuthCookies } from '@/modules/users/service/auth'
import { signOut } from 'firebase/auth'
import { auth } from '@/config/firebaseApp'
import { errorHandler } from '@/config/errorReporting'
import { useRouter } from 'next/navigation'
import { IUserRole } from '@/modules/users/domain/IUser'
import { IBase } from '@umahealth/entities'
import { AdminRoles } from '@/config/adminRoles'
import { INDICADORES_POR_LINEA_ROLES, SUPERVISION_DASHBOARD_ROLES, esSoloSupervisorDeLinea, isSupervisorProfile } from '@/config/permissions'
import { userHasRole } from '@/utils/functions'
import { useCollaboratorSessionContext } from '@/context/CollaboratorSessionContext'
import BaseSelectModal from './BaseSelectModal'
import MenuPorSecciones, { ElementoDeMenu } from './MenuPorSecciones'

type HeaderProps = {
    logged: boolean,
    /** null cuando la API no responde: el Header no se renderiza. */
    currentCompany: Company | null
    userRoles: IUserRole[] | null
    bases?: IBase[]
    /** La base donde el enfermero está asignado: se ofrece preseleccionada. */
    baseHabitual?: number | null
}

/**
 * Las secciones del menú, en orden de display.
 *
 * Existen para el perfil administrativo, que ve nueve links y los veía todos
 * en una lista plana. Un enfermero ve dos y no necesita títulos: por eso el
 * agrupado se enciende solo, mirando cuántas secciones quedaron visibles, en
 * vez de preguntar por el rol. Así ningún rol nuevo se queda afuera por
 * olvido.
 */
/**
 * A dónde va "Ingreso Portal Médicos": a la otra aplicación, no a una ruta de
 * ésta.
 *
 * Apuntaba a `/ingreso-portal-medicos`, una ruta que **nunca existió**, así que
 * el item daba 404 en producción. La variable ya está en los `.env` de los tres
 * ambientes y la usa el login para ofrecerle su link a un médico que entró al
 * portal equivocado; es la misma URL.
 *
 * Si faltara, el item no se dibuja: mejor sin item que con un link roto.
 */
const URL_DEL_PORTAL_DE_MEDICOS = process.env.NEXT_PUBLIC_ONLINEDOCTOR_URL ?? '';

const SECCIONES = {
	OPERACION: 'Operación',
	ADMINISTRACION: 'Administración',
	ACCESOS: 'Accesos',
} as const;

const ORDEN_DE_SECCIONES = [
	SECCIONES.OPERACION,
	SECCIONES.ADMINISTRACION,
	SECCIONES.ACCESOS,
] as const;

// Define a type for the link objects
type LinkItem = {
	appModuleId?: number;
	href: string;
	text: string;
	isExternal?: boolean;
	/** icon of the element */
	icon: IIcon["name"];
	/** If set, link is visible only for these roles (bypasses appModule check) */
	requiredRoles?: AdminRoles[];
	/** Sección del menú. Sin sección, el link va suelto arriba de todo. */
	seccion?: (typeof ORDEN_DE_SECCIONES)[number];
};

const links: LinkItem[] = [
	{ appModuleId: appModules.OPERACIONES, href: "/", text: "Inicio", icon: "home" },
	{
		appModuleId: appModules.OPERACIONES,
		href: "/operaciones",
		text: "Evaluaciones",
		icon: "file",
		seccion: SECCIONES.OPERACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/monitoreo",
		text: "Monitoreo",
		// Era "user", el mismo dibujo que Usuarios para dos cosas distintas.
		// Monitoreo son tableros y números, no personas.
		icon: "barChart",
		requiredRoles: [...SUPERVISION_DASHBOARD_ROLES],
		seccion: SECCIONES.OPERACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/indicadores-por-linea",
		text: "Indicadores por línea",
		// Administración y supervisor de línea. La página y la API verifican
		// lo mismo; esconder el item no protege nada por sí solo.
		icon: "trendingUp",
		requiredRoles: [...INDICADORES_POR_LINEA_ROLES],
		seccion: SECCIONES.OPERACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/protocolos-en-papel",
		// Era "file", repetido con Evaluaciones. Acá lo que se hace es subir un
		// archivo escaneado, y el icono ahora lo dice.
		icon: "uploadFile",
		text: "Protocolos en papel",
		// Los archivos traen nombre, documento y el resultado clínico de una
		// persona. Quien decide de verdad es `SoloAdminGuard` en el backend:
		// esconder el item del menú no protege nada por sí solo.
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.OWNER],
		seccion: SECCIONES.OPERACION,
	},
	{
		appModuleId: appModules.USUARIOS,
		href: "/usuarios",
		text: "Usuarios",
		icon: "userPlus",
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.OWNER, AdminRoles.SOPORTE],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.BASES,
		href: "/bases",
		text: "Bases",
		icon: "hospital",
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.OWNER, AdminRoles.SOPORTE],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/psa",
		text: "PSA",
		icon: "medicalInfo",
		// El mismo corte que el backend: el seguimiento lleva el estado de PSA de
		// cada persona.
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_MEDICO],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/etilometros",
		text: "Etilómetros",
		icon: "science",
		// El mismo corte que `SoloAdminGuard`, que es quien decide en el backend.
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.OWNER],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/sigof",
		text: "SIGOF",
		icon: "train",
		// Como `canSeeSigof` y `SeguimientoDeSigofGuard` en el backend.
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_OPERATIVO],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/formularios",
		text: "Formularios",
		// Era "description", casi igual al "file" de Evaluaciones.
		icon: "note",
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.OWNER, AdminRoles.SOPORTE],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: "/auditoria",
		text: "Auditoría",
		// Un registro de lo que pasó, no una acción: el icono es de lectura.
		icon: "newspaper",
		// El mismo corte que en el backend. El supervisor médico entra porque
		// quien puede cambiar un resultado tiene que poder ver quién más lo hizo.
		requiredRoles: [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_MEDICO],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.USUARIOS,
		href: "/notificaciones-bajas",
		text: "Mails de bajas",
		icon: "email",
		requiredRoles: [
			AdminRoles.ADMIN,
			AdminRoles.OWNER,
			AdminRoles.SOPORTE,
			AdminRoles.SUPERVISOR_MEDICO,
		],
		seccion: SECCIONES.ADMINISTRACION,
	},
	{
		appModuleId: appModules.OPERACIONES,
		href: URL_DEL_PORTAL_DE_MEDICOS,
		isExternal: true,
		text: "Ingreso Portal Medicos",
		icon: "stethoscope",
		requiredRoles: [AdminRoles.ADMIN],
		seccion: SECCIONES.ACCESOS,
	},
];

function CollaboratorStatusToggle() {
	const session = useCollaboratorSessionContext()
	if (!session) return null
	// Se esconde sólo cuando de verdad no hay turno. Si no se pudo leer el
	// estado, se muestra igual y se dice que no se sabe: esconderlo dejaba a la
	// persona sin forma de arreglarlo y sin saber que había algo que arreglar.
	if (session.statusConocido && session.status === 'offline') return null

	const sinDato = !session.statusConocido

	return (
		<li className="w-full px-3 py-1">
			<button
				onClick={session.togglePause}
				disabled={session.isLoading}
				className={cn(
					"flex items-center gap-2 w-full px-3 py-2 rounded-xl transition-colors hover:bg-primary-25",
					session.isLoading && "opacity-50 cursor-wait"
				)}
			>
				<span
					className={cn(
						"w-2.5 h-2.5 rounded-full flex-shrink-0",
						sinDato
							? "bg-grey-300"
							: session.isPaused
								? "bg-yellow-400"
								: "bg-green-500"
					)}
				/>
				<span className="group-[.isClosed]:sr-only">
					{sinDato ? "Estado sin confirmar" : session.isPaused ? "Pausado" : "Disponible"}
				</span>
			</button>
		</li>
	)
}

function CollaboratorBaseDisplay({
	bases,
	baseHabitual,
}: {
	bases?: IBase[];
	baseHabitual?: number | null;
}) {
	const session = useCollaboratorSessionContext()
	const [showModal, setShowModal] = useState(false)

	if (!session || !bases?.length) return null
	// Mismo criterio que el indicador: no se esconde por no poder leer el
	// estado. Sin esto, el enfermero no tenía dónde declarar su base.
	if (session.statusConocido && session.status === 'offline') return null

	return (
		<>
			<li className="w-full px-3 py-1">
				<button
					onClick={() => setShowModal(true)}
					className="flex items-center gap-2 w-full px-3 py-2 rounded-xl transition-colors hover:bg-primary-25 text-left"
				>
					<Icon name="home" size="size-4" color="text-grey-400" />
					<span className="group-[.isClosed]:sr-only text-xs text-grey-500 truncate">
						{session.currentBase?.name ?? "Sin base"}
					</span>
				</button>
			</li>
			{showModal && (
				<BaseSelectModal
					bases={bases}
					currentBaseId={session.currentBaseId}
					baseHabitual={baseHabitual}
					onSelect={(baseId) => {
						session.changeBase(baseId)
						setShowModal(false)
					}}
					onClose={() => setShowModal(false)}
				/>
			)}
		</>
	)
}

/**
 * La pregunta del comienzo del turno.
 *
 * Dispara cuando la sesión no tiene lugar declarado. Antes no disparaba nunca,
 * porque el backend copiaba la base fija de la persona al abrir el turno: por
 * eso el historial mostraba siempre dónde está asignada y no dónde trabajó.
 */
function CollaboratorBasePrompt({
	bases,
	baseHabitual,
}: {
	bases?: IBase[];
	baseHabitual?: number | null;
}) {
	const session = useCollaboratorSessionContext()
	const [showPrompt, setShowPrompt] = useState(false)

	useEffect(() => {
		if (session && session.isOnline && !session.currentBaseId && bases?.length) {
			setShowPrompt(true)
		}
	}, [session?.isOnline, session?.currentBaseId, bases?.length])

	if (!showPrompt || !session || !bases?.length) return null

	return (
		<BaseSelectModal
			bases={bases}
			currentBaseId={session.currentBaseId}
			baseHabitual={baseHabitual}
			abriendoTurno
			onSelect={(baseId) => {
				session.changeBase(baseId)
				setShowPrompt(false)
			}}
			onClose={() => setShowPrompt(false)}
		/>
	)
}

export default function Header({ logged, currentCompany, userRoles, bases, baseHabitual }:HeaderProps ) {
	const router = useRouter()
	const isCollaborator = userHasRole(userRoles, AdminRoles.COLLABORATOR)
	// Los dos perfiles de supervisor, más el rol 3 que quedó como legacy.
	const isSupervisor = isSupervisorProfile(userRoles)
	const isSoporte = userHasRole(userRoles, AdminRoles.SOPORTE)
	const soloSupervisorDeLinea = esSoloSupervisorDeLinea(userRoles)

    /**
     * Cerrar sesión, de verdad.
     *
     * Antes sólo borraba las cookies: la sesión de Firebase seguía viva en el
     * navegador. En una máquina compartida entre turnos eso no es lo que uno
     * espera del botón, y además alcanzaba con que algo volviera a escribir la
     * cookie para quedar logueado de nuevo.
     *
     * Primero se cierra la sesión de Firebase y después se borran las cookies,
     * así no queda un momento con sesión viva y cookie fresca. Si el `signOut`
     * falla igual se borran las cookies, que es lo que el servidor mira.
     */
    const handleSignOut = async () => {
        try {
            await signOut(auth)
        } catch (err) {
            errorHandler?.report(`No se pudo cerrar la sesión de Firebase: ${err}`)
        }
        await removeAuthCookies()
		router.push('/usuarios/acceso')
    }

    if (!logged || !currentCompany) {
        return null
    }

	const canSeeLink = (link: LinkItem) => {
		// SOPORTE sólo ve links que listen explícitamente el rol SOPORTE en requiredRoles
		if (isSoporte) {
			return !!link.requiredRoles?.includes(AdminRoles.SOPORTE)
		}
		// El supervisor de línea, igual: sólo lo que lo nombra. Si no, vería
		// Operaciones, que no tiene requiredRoles y de la que igual se lo saca.
		if (soloSupervisorDeLinea) {
			return !!link.requiredRoles?.includes(AdminRoles.SUPERVISOR_LINEA)
		}
		if (link.requiredRoles) {
			return link.requiredRoles.some((role) => userHasRole(userRoles, role))
		}
		if (link.appModuleId === appModules.USUARIOS && (isCollaborator || isSupervisor)) {
			return false
		}
		return link.appModuleId != null && companyCanAccessModule(currentCompany, link.appModuleId)
	}

	const linksVisibles = links.filter(
		(link) => canSeeLink(link) && link.href !== '',
	)

	/**
	 * El menú se agrupa solo: si de todo el listado quedó visible una sola
	 * sección, no hay nada que separar y se dibuja la lista de siempre. Un
	 * enfermero ve Inicio y Evaluaciones; ponerle el título "Operación" arriba
	 * de un solo item es ruido.
	 */
	const seccionesVisibles = ORDEN_DE_SECCIONES.map((titulo) => ({
		titulo,
		items: linksVisibles
			.filter((link) => link.seccion === titulo)
			.map(
				({ href, text, icon, isExternal }): ElementoDeMenu => ({
					href,
					text,
					icon,
					externo: isExternal,
				}),
			),
	})).filter((seccion) => seccion.items.length > 0)

	const menuPorSecciones = seccionesVisibles.length > 1
	const sueltos = linksVisibles
		.filter((link) => !link.seccion)
		.map(
			({ href, text, icon, isExternal }): ElementoDeMenu => ({
				href,
				text,
				icon,
				externo: isExternal,
			}),
		)
	const secciones = seccionesVisibles

    return (
		<>
		<SidebarContainer
		logoVisibility='hideOnClose'
		arrowPosition='right'
			src={{
				src: currentCompany.logo,
				height: 62,
				width: 196,
			}}
			data-cy={`header-container`}
		>
			<small className="text-grey-400 -mt-2 text-center w-full group-[.isClosed]:hidden">by UMA Health AI</small>
			{menuPorSecciones ? (
				<MenuPorSecciones sueltos={sueltos} secciones={secciones} />
			) : (
				linksVisibles.map((link) => (
					<SidebarElement
						key={link.text}
						name={link.icon}
						href={link.href}
						data-cy={`header-${link.text}-link`}
					>
						{link.text}
					</SidebarElement>
				))
			)}
			{/*
			  * El espaciador es el que empuja todo lo que sigue al pie del menú.
			  *
			  * Antes el `mt-auto` lo tenía el botón de cerrar sesión. Los controles
			  * del turno quedaban pegados a los links, arriba, y no se leían como
			  * lo que son: el estado de la persona, no una sección más del menú.
			  *
			  * Va acá y no en el primer control del grupo porque los dos pueden no
			  * renderizarse (sin sesión de colaborador, o fuera de turno). Con el
			  * `mt-auto` en el espaciador el bloque queda al pie igual, aunque
			  * dentro sólo quede el cerrar sesión.
			  */}
			<li className="mt-auto" aria-hidden />
			{isCollaborator && <CollaboratorStatusToggle />}
			{isCollaborator && (
				<CollaboratorBaseDisplay bases={bases} baseHabitual={baseHabitual} />
			)}
			<li className="w-[149px]  h-14 mb-6">
				<button
					className={cn(
						"flex align-middle content-center group-[.isClosed]:justify-center justify-start items-center py-4 px-4 hover:bg-primary-25 rounded-2xl"
					)}
					onClick={() => {
						handleSignOut();
					}}
				>
					<Icon
						size="size-6"
						className="mr-2"
						color="text-grey-500"
						name="logout"
					/>
					<span className="group-[.isClosed]:sr-only">
						Cerrar
					</span>
				</button>
			</li>
		</SidebarContainer>
		{isCollaborator && (
			<CollaboratorBasePrompt bases={bases} baseHabitual={baseHabitual} />
		)}
		</>
	);
}
